import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { ExternalLink, Zap, Clock, ArrowLeft } from "lucide-react";

export default function PublicProfile() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/auth/users/${userId}/public`);
        if (!cancelled) setData(data);
      } catch (err) {
        if (!cancelled) setError(formatApiError(err.response?.data?.detail) || "Utente non trovato");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const initials = data ? `${(data.first_name?.[0] || "")}${(data.last_name?.[0] || "")}`.toUpperCase() : "?";

  return (
    <>
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <button onClick={() => window.history.back()} className="text-xs uppercase tracking-wider font-bold text-gray-600 hover:text-gray-900 flex items-center gap-1.5" data-testid="public-profile-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Indietro
        </button>

        {loading && (
          <div className="tactical-card rounded-sm p-12 text-center text-gray-500 text-sm">Caricamento...</div>
        )}

        {!loading && error && (
          <div className="tactical-card rounded-sm p-12 text-center" data-testid="public-profile-not-found">
            <div className="text-gray-700 font-bold mb-2">{error}</div>
            <Link to="/feed" className="text-xs uppercase tracking-wider text-[#E11D48] hover:underline">← Torna al feed</Link>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="tactical-card rounded-sm p-6 md:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-5">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100 flex items-center justify-center flex-shrink-0" data-testid="public-profile-avatar">
                {data.picture ? (
                  <img src={data.picture} alt={`${data.first_name}`} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display font-black text-3xl text-gray-400 tracking-tighter">{initials}</span>
                )}
              </div>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="label-tag mb-2">Profilo pubblico</div>
                <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter text-gray-900 truncate" data-testid="public-profile-name">
                  {data.first_name} {data.last_name}
                </h1>
                {data.bio && (
                  <p className="text-gray-500 mt-3 text-sm whitespace-pre-wrap" data-testid="public-profile-bio">{data.bio}</p>
                )}
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-4">
              {data.twenty_min_power > 0 && (
                <div className="tactical-card rounded-sm p-5">
                  <div className="label-tag mb-1 flex items-center gap-1.5"><Zap className="w-3 h-3 text-[#E11D48]" /> 20-min Power</div>
                  <div className="font-display font-black text-3xl text-gray-900" data-testid="public-profile-power">{data.twenty_min_power}<span className="text-base text-gray-500"> W</span></div>
                </div>
              )}
              {data.monthly_hours > 0 && (
                <div className="tactical-card rounded-sm p-5">
                  <div className="label-tag mb-1 flex items-center gap-1.5"><Clock className="w-3 h-3 text-[#E11D48]" /> Ore in sella/mese</div>
                  <div className="font-display font-black text-3xl text-gray-900" data-testid="public-profile-hours">{data.monthly_hours}<span className="text-base text-gray-500"> h</span></div>
                </div>
              )}
              {data.twenty_min_power === 0 && data.monthly_hours === 0 && (
                <div className="tactical-card rounded-sm p-5 col-span-2 text-center text-sm text-gray-500">
                  Questo ciclista non ha ancora inserito le proprie statistiche.
                </div>
              )}
            </div>

            {/* Strava */}
            {data.strava_connected && data.strava_url && (
              <a
                href={data.strava_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="public-profile-strava-link"
                className="tactical-card rounded-sm p-5 flex items-center justify-between gap-3 hover:border-[#FC4C02] transition-colors group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-sm bg-[#FC4C02] flex-shrink-0 flex items-center justify-center text-white font-display font-black text-lg uppercase tracking-tighter">S</div>
                  <div className="min-w-0">
                    <div className="font-display font-black uppercase tracking-tighter text-gray-900">Profilo Strava</div>
                    <div className="text-xs text-gray-500 truncate">Apri su strava.com — la visibilità dipende dalle impostazioni privacy dell'utente</div>
                  </div>
                </div>
                <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-[#FC4C02] flex-shrink-0" />
              </a>
            )}

            {!data.strava_connected && (
              <div className="tactical-card rounded-sm p-5 text-center text-sm text-gray-500" data-testid="public-profile-strava-empty">
                Questo ciclista non ha collegato Strava.
              </div>
            )}
          </>
        )}
      </main>
    </>
  );
}
